import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Icon } from "@/components/Icon"
import { apiClient } from "@/services/api.client"
import { isMockReplay } from "@/features/oio/mock/mockMode"

import { useOIOActions } from "@/features/oio/actions/oio.actions"
import { useRailWhileGenerating } from "@/shell/useRailWhileGenerating"
import { useOIOAgent } from "@/features/oio/hooks/useOIOAgent"
import { useOIORewriteAgent } from "@/features/oio/hooks/useOIORewriteAgent"
import { useOIORewriteHITL } from "@/features/oio/hooks/useOIORewriteHITL"
import { useOIOToolRendering } from "@/features/oio/hooks/useOIOToolRendering"
import { useRewriteCompletion } from "@/features/oio/hooks/useRewriteCompletion"
import { useDraftingSession } from "@/features/drafting/hooks/useDraftingSession"
import { useDraftStreamReconnect } from "@/features/drafting/hooks/useDraftStreamReconnect"
import { hydrateFinishedDraft } from "@/features/drafting/services/draft-hydration.service"
import { useCaseDraftStatus } from "@/features/oio/hooks/useCaseDraftStatus"
import { readDraftText, markNewAttempt, overwriteDraftText } from "@/features/oio/services/draft-storage.service"
import { applyDocumentEdit, type DocumentEdit } from "@/features/workspace/lib/paragraph-edit"
import type { DocumentEditing } from "@/features/workspace/components/DocumentRenderer"
import { showSection } from "@/features/workspace/lib/locate"
import { OioNextStepsPanel } from "./OioNextStepsPanel"
import {
  buildOioNextSteps,
  fixInstruction,
  mustSettleCount,
  openSteps,
  readDecisions,
  type OioNextStep,
  type OioStepDecision,
} from "../lib/next-steps"
import type { ChangedRange } from "@/features/workspace/lib/diffRangeHighlight"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { useAnalysisStore } from "@/features/analysis/store/analysis.store"
import { useDraftingStore } from "@/features/drafting/store/drafting.store"
import { useThreadContext } from "@/providers/ThreadProvider"
import { OIO_STAGES } from "@/features/oio/config/oio.config"
import { deriveActiveStage } from "@/features/oio/hooks/useLiveThoughts"

import type { Allegation } from "@/types"

import { CaseFileUploader } from "@/features/file-review/components/CaseFileUploader"
import { DocumentRenderer } from "@/features/workspace/components/DocumentRenderer"
import { ShareMenu } from "./ShareMenu"
import { DocViewerModal, type ViewableFile } from "./DocViewerModal"
import { parseAlignedAllegations, type ParsedAllegation } from "@/features/oio/lib/allegations"
import {
  findingsToAllegations,
  parseFindingsSummary,
  type FindingsSummaryItem,
} from "@/features/oio/lib/findings"
import { CitationReviewChips } from "./CitationReviewChips"
import { LiveFindingsFeed } from "./LiveFindingsFeed"
import { OrderSummaryCard } from "./OrderSummaryCard"
import { useReviewStore } from "@/features/oio/store/review.store"
import { useRetryStore } from "@/features/oio/errors/retry.store"
import { useGenerationStore, runFor } from "@/features/oio/generation.store"
import { useErrorStore, ReportIssueClient } from "@/features/oio/errors"
import { casesService } from "@/features/cases/services/cases.service"
import { useCopilotChatInternal } from "@copilotkit/react-core"
import { TextMessage, Role } from "@copilotkit/runtime-client-gql"
import { SelectionToolbar } from "./SelectionToolbar"
import { ApprovalPanel } from "./ApprovalPanel"
import { useHITLStore } from "../store/hitl.store"
import { DraftGenerationPane } from "./DraftGenerationPane"
import { RewriteChatPanel } from "./RewriteChatPanel"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { acknowledgeSimulatorHandoff, claimSimulatorHandoff, readSimulatorHandoff } from "@/features/drafting/simulatorHandoff"
import type { PreparedOioInput } from "@/features/drafting/simulatorHandoff"

// MOCK REPLAY: lazy so the mock chunk (incl. the recorded fixture) stays out
// of the real path's bundle — only ever rendered when isMockReplay().
// Dev-gated lazy import: in a production build import.meta.env.DEV is false,
// so this ternary is dead code and Rollup drops the mock chunk (+ fixture).
const MockSeededUploader = import.meta.env.DEV
  ? lazy(() =>
      import("@/features/oio/mock/MockSeededUploader").then((m) => ({
        default: m.MockSeededUploader,
      })),
    )
  : (() => null)

type LeftTab = "chat" | "next" | "analysis"

interface DocRef {
  url: string
  docType: "scn_document" | "reply_document" | "supporting_document"
  filename: string
}

/**
 * Builds the user message sent to NitiassistAgent. The first line is a JSON
 * metadata block the backend parses to extract doc_urls; it is stripped before
 * display in chat (see ChatPanel).
 *
 * replyUrl is null for ex-parte cases — the backend accepts SCN-only doc_urls
 * and records reply_status='not_received' at ingest, so no reply_document
 * entry is sent at all.
 */
export function buildPrompt(
  scnUrl: string,
  replyUrl: string | null,
  supportingUrls: string[] = [],
  scnFileName = "",
  replyFileName = "",
  supportingFileNames: string[] = [],
): string {
  const docRefs: DocRef[] = [
    { url: scnUrl, docType: "scn_document", filename: scnFileName },
    ...(replyUrl
      ? [{ url: replyUrl, docType: "reply_document" as const, filename: replyFileName }]
      : []),
    ...supportingUrls.map((url, i) => ({
      url,
      docType: "supporting_document" as const,
      filename: supportingFileNames[i] ?? `supporting_${i + 1}`,
    })),
  ]
  const meta = JSON.stringify({ __doc_urls: docRefs })
  return [
    meta,
    "Please draft a comprehensive Order-in-Original (OIO) for this GST case.",
    "Follow the CBIC format. Analyse all allegations, map amounts, apply relevant legal provisions, and draft findings for each allegation before writing the final order.",
  ].join("\n")
}

// FindingsSummaryItem, mapDeterminationStatus and findingsToAllegations moved
// to @/features/oio/lib/findings — shared with the live findings feed and the
// completion summary card.

/** Early aligned allegations → Allegation rows (still "under review", no demand yet). */
function alignedToAllegations(parsed: ParsedAllegation[]): Allegation[] {
  return parsed.map((a) => ({
    id: a.id,
    allegation: a.title,
    status: "unknown" as Allegation["status"],
    amount: undefined,
    section: a.provisions?.[0] || undefined,
  }))
}

// The CopilotKit session is mounted once at the shell (AppShell) so it survives
// navigation; this workspace just consumes it.
export function OIOWorkspace() {
  return <OIOWorkspaceContent />
}

function OIOWorkspaceContent() {
  const shouldEditOnUpdateRef = useRef(false)
  const handleReviseTrigger = useCallback(() => {
    shouldEditOnUpdateRef.current = true
  }, [])

  const hitlNode = useOIORewriteHITL(handleReviseTrigger)
  useOIOToolRendering()
  useRewriteCompletion()
  useOIOActions()

  const { activeThread, setActiveServiceId, setActiveThread, refreshThreads } = useThreadContext()
  const { session } = useAuth()

  const { start, isActive } = useDraftingSession()
  const draftStatus = useCaseDraftStatus()
  const { state: agent, setState: setOIOState, run: runOio, running: oioRunning } = useOIOAgent()
  const rewriteAgent = useOIORewriteAgent()
  const {
    rewrite_status: rewriteStatus,
    current_stage: currentStage,
    run: runRewrite,
    running: rewriteRunning,
    reattach: reattachRewrite,
    resume: resumeRewrite,
  } = rewriteAgent
  // Mid-review evidence: attach a file in the rewrite chat; the backend runs
  // one multimodal first-pass index (PageIndex-style) and persists only the
  // compact summary into the case context — the raw file never enters the
  // chat context. On success the agent is asked to assess the new evidence.
  const [evidenceUploading, setEvidenceUploading] = useState(false)
  const handleAttachEvidence = useCallback(
    async (file: File) => {
      const threadId = activeThread?.id
      if (!threadId || evidenceUploading) return
      setEvidenceUploading(true)
      try {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("thread_id", threadId)
        const result = await apiClient.upload<{
          name: string
          document_type: string
          summary: string
        }>("/api/v1/storage/evidence", formData)
        void runRewrite(
          `I have added new evidence to the record: "${result.name}"` +
            (result.document_type ? ` (${result.document_type})` : "") +
            ". Review it — its index is under /workspace/new_evidence/ — and " +
            "tell me how it bears on the findings before we change anything.",
        )
      } catch (err) {
        useErrorStore.getState().addError({
          code: "evidence-upload-failed",
          severity: "transient",
          category: "persist",
          userMessage:
            "Evidence upload failed: " +
            (err instanceof Error ? err.message : "the file could not be added.") +
            " Try again, or paste the key contents into the chat.",
          retryable: true,
          threadId,
          timestamp: Date.now(),
          source: "http",
        })
      } finally {
        setEvidenceUploading(false)
      }
    },
    [activeThread?.id, evidenceUploading, runRewrite],
  )

  const hitlProposal = useHITLStore((s) => s.proposal)
  const isApplying = useHITLStore((s) => s.isApplying)
  const showRejectFollowup = useHITLStore((s) => s.showRejectFollowup)
  const setShowRejectFollowup = useHITLStore((s) => s.setShowRejectFollowup)

  // Reattach to an in-progress backend run on mount — see
  // docs/superpowers/specs/2026-06-20-draft-stream-reconnect-design.md.
  // Only reattach if the draft is already marked 'draft_running' on the backend.
  // Never fire on 'draft_starting' (which is set when the officer clicks Generate Draft,
  // before appendMessage has sent the prompt and documents).
  useDraftStreamReconnect({
    agentName: "oio",
    threadId: activeThread?.id,
    isTerminal: draftStatus !== "draft_running",
    agent: { run: runOio, running: oioRunning },
  })
  useDraftStreamReconnect({
    agentName: "oio_rewrite",
    threadId: activeThread?.id,
    isTerminal: rewriteStatus !== "running",
    agent: { run: runRewrite, reattach: reattachRewrite, running: rewriteRunning },
  })

  // Tell the OIO graph which nitiassist thread it's drafting for, BEFORE a run
  // starts. CopilotKit runs the graph under its own generated threadId (not the
  // case id), so without this the backend has no way to PATCH draft_status /
  // artifactBlobUrl back to THIS thread on completion — which is exactly what a
  // reconnecting client (closed tab / asleep / redeployed) reads to catch up.
  // ingest/compile prefer typed_state.thread_id, so this wins over the run id.
  useEffect(() => {
    const id = activeThread?.id
    if (!id || draftStatus !== "draft_not_started" || agent.thread_id === id) return
    setOIOState((prev) => ({ ...(prev ?? agent), thread_id: id }))
  }, [activeThread?.id, draftStatus, agent.thread_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const orderDraftStore = useOrderDraftStore()
  const draftingStore = useDraftingStore()
  const analysisStore = useAnalysisStore()

  const [leftTab, setLeftTab] = useState<LeftTab>("chat")
  const [viewFile, setViewFile] = useState<ViewableFile | null>(null)

  const scnFileName = activeThread?.metadata?.scnFileName as string | undefined
  const replyFileName = activeThread?.metadata?.replyFileName as string | undefined
  const scnFileUrl = activeThread?.metadata?.scnFileUrl as string | undefined
  const replyFileUrl = activeThread?.metadata?.replyFileUrl as string | undefined

  const isUserEdited = useRef(false)
  const blobUrlRef = useRef<string | null>(null)
  // Monotonic token so only the most-recently-requested draft load applies its
  // result — see the hydration effect below.
  const draftLoadTokenRef = useRef(0)

  // Text selection state
  const docPaneRef = useRef<HTMLDivElement>(null)
  const [selectionRect, setSelectionRect] = useState<{ top: number; left: number } | null>(null)
  const [selectedText, setSelectedText] = useState("")
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false)

  // Flash highlight state
  const [justUpdated, setJustUpdated] = useState(false)
  const prevRewriteStatus = useRef(rewriteStatus)

  // Which lines changed in the most recently committed rewrite, so
  // DocumentRenderer can highlight them. Prefer the live value from the
  // rewrite hook (populated from STATE_SNAPSHOT right after an approval
  // commits, while the SSE connection is live) — the cold-load fetch below
  // only fills this in when there's no live value yet (fresh mount / reload /
  // reconnect), and never overwrites a live value that already landed.
  const [changedRanges, setChangedRanges] = useState<ChangedRange[] | null>(null)

  // ── Resizable split (drag the divider; persisted) ───────────────────────
  const splitRef = useRef<HTMLDivElement>(null)
  const [leftWidth, setLeftWidth] = useState<number | null>(() => {
    const saved = Number(localStorage.getItem("oio-split-left"))
    return Number.isFinite(saved) && saved > 0 ? saved : null
  })
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const el = splitRef.current
    if (!el) return
    el.classList.add("is-resizing")
    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"
    const onMove = (ev: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      // Clamp so neither pane gets unusably narrow (degrades gracefully when the
      // window itself is narrow — the upper bound never drops below the min).
      const upper = Math.max(300, rect.width - 360)
      const w = Math.max(300, Math.min(ev.clientX - rect.left, upper))
      setLeftWidth(w)
    }
    const onUp = () => {
      el.classList.remove("is-resizing")
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      setLeftWidth((w) => {
        if (w) localStorage.setItem("oio-split-left", String(Math.round(w)))
        return w
      })
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }, [])

  // On case switch: reset stores and the user-edit flag, restore from localStorage.
  useEffect(() => {
    if (!activeThread?.id) return
    setActiveServiceId("oio")
    isUserEdited.current = false
    blobUrlRef.current = null
    setSelectionRect(null)
    setSelectedText("")
    orderDraftStore.reset()
    draftingStore.reset()
    analysisStore.reset()
    setChangedRanges(null)
    useReviewStore.getState().reset()

    // Restore the review layer (citation verification states, threshold
    // findings) alongside the analysis, same persistence pattern.
    const savedReview = localStorage.getItem(`oio-review-${activeThread.id}`)
    if (savedReview) useReviewStore.getState().setFromJson(savedReview)

    // Restore the Analysis tab (allegation-wise findings + demand) so it
    // survives navigation/reload — the live agent state may not re-emit it on
    // reconnect. Prefer full findings; fall back to the early aligned set.
    try {
      const savedFindings = localStorage.getItem(`oio-findings-${activeThread.id}`)
      if (savedFindings) {
        const findings = JSON.parse(savedFindings) as FindingsSummaryItem[]
        if (Array.isArray(findings) && findings.length > 0) {
          analysisStore.setAnalysisData({ findings_summary: findingsToAllegations(findings) })
        }
      } else {
        const savedAllegs = localStorage.getItem(`oio-allegs-${activeThread.id}`)
        if (savedAllegs) {
          const parsed = parseAlignedAllegations(JSON.parse(savedAllegs) as string[])
          if (parsed.length > 0) {
            analysisStore.setAnalysisData({ findings_summary: alignedToAllegations(parsed) })
          }
        }
      }
    } catch (err) {
      console.error("[OIOWorkspace] analysis restore failed:", err)
    }
    setLeftTab("chat")
    setSelectionRect(null)
    setSelectedText("")
    setIsToolbarExpanded(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.id])

  // Fetch draft content from GCS whenever artifact_blob_url changes.
  // artifact_blob_url is the single source of truth — no localStorage cache.
  //
  // Do NOT gate the result on a per-run `cancelled` cleanup: this effect's deps
  // are the metadata blob URLs, and the agent/rewrite-snapshot effects below
  // rewrite `activeThread.metadata.artifact_blob_url` during load. That churn
  // re-runs this effect, and a cleanup-based cancel would then discard the
  // in-flight (already 200) fetch — so a completed draft renders as "not
  // available" and rewrite can't start. Instead: claim the URL in blobUrlRef so
  // benign re-renders dedupe (no duplicate fetch, no cancel), and guard the
  // result with a monotonic token so a genuinely newer URL still wins the race.
  useEffect(() => {
    const id = activeThread?.id
    const blobUrl =
      (activeThread?.metadata?.artifact_blob_url as string | undefined) ??
      (activeThread?.metadata?.artifactBlobUrl as string | undefined)
    if (!id || !blobUrl) return
    // MOCK REPLAY: mock:// artifacts are deliberately unfetchable — the draft
    // content already arrived through agent state (agent.oio_draft), so a
    // storage read here would be both a network call and a guaranteed miss.
    // Real backends never emit mock:// URLs, so this is inert on the live path.
    if (blobUrl.startsWith("mock://")) return
    if (blobUrlRef.current === blobUrl) return

    blobUrlRef.current = blobUrl
    const token = ++draftLoadTokenRef.current
    void hydrateFinishedDraft(id, blobUrl).then((content) => {
      if (token !== draftLoadTokenRef.current) return // superseded by a newer load
      if (content) {
        // The rewrite session usually delivered the same markdown moments
        // earlier — re-setting an identical multi-hundred-KB string forces a
        // second full markdown parse for nothing. String equality is a cheap
        // memcmp next to that parse.
        if (content !== orderDraftStore.content) {
          orderDraftStore.setContent(content)
        }
      } else if (blobUrlRef.current === blobUrl) {
        blobUrlRef.current = null // release the claim so a later attempt can retry
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.id, activeThread?.metadata?.artifact_blob_url, activeThread?.metadata?.artifactBlobUrl])

  // Switch to oio_rewrite agent once the initial draft is done.
  useEffect(() => {
    if (draftStatus === "draft_completed") setActiveServiceId("oio_rewrite")
  }, [draftStatus, setActiveServiceId])

  // Sync agent draft (from LangGraph checkpoint via useCoAgent).
  useEffect(() => {
    if (!agent.oio_draft || !activeThread?.id) return
    if (agent.oio_draft === orderDraftStore.content) return

    if (isActive) {
      isUserEdited.current = false
      orderDraftStore.setContent(agent.oio_draft)
    } else {
      orderDraftStore.setContent(agent.oio_draft)
    }
  }, [agent.oio_draft, activeThread?.id, isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync per-allegation findings from checkpoint → analysisStore, and persist
  // them per case so the Analysis tab (with determinations + demand) survives
  // navigation, reload and the reconnection gap.
  useEffect(() => {
    if (!agent.findings_summary_json || !activeThread?.id) return
    const threadId = activeThread.id
    try {
      const findings = JSON.parse(agent.findings_summary_json) as FindingsSummaryItem[]
      if (!Array.isArray(findings) || findings.length === 0) return
      analysisStore.setAnalysisData({ findings_summary: findingsToAllegations(findings) })
      localStorage.setItem(`oio-findings-${threadId}`, agent.findings_summary_json)
      // Persist the case-level total demand onto the thread so Home/Vault
      // lists (which only have the Thread, not the findings) can show it.
      // Metadata PATCH is a whole-object replace, so merge from a fresh read
      // to avoid clobbering fields the backend wrote mid-run.
      const total = findings.reduce(
        (sum, f) => sum + (Number(f.demand_computation?.total_demand) || 0),
        0,
      )
      const formatted = total > 0 ? "₹ " + total.toLocaleString("en-IN") : null
      const metaNow = (activeThread.metadata ?? {}) as Record<string, unknown>
      // MOCK REPLAY: the demand_amount metadata PATCH is a backend write —
      // skip it entirely (the analysis store + localStorage above still
      // populate, which is all the mock UI needs).
      if (formatted && metaNow.demand_amount !== formatted && !isMockReplay()) {
        casesService
          .get(threadId)
          .then((fresh) =>
            casesService.update(threadId, {
              metadata: {
                ...((fresh.metadata ?? {}) as Record<string, unknown>),
                demand_amount: formatted,
              },
            }),
          )
          .then(() => refreshThreads())
          .catch((err) =>
            console.error("[OIOWorkspace] demand_amount metadata persist failed:", err),
          )
      }
    } catch (err) {
      console.error("[OIOWorkspace] findings_summary_json parse failed:", err)
    }
  }, [agent.findings_summary_json, activeThread?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the review layer (second artifact: citation verification states,
  // threshold findings, gate results) from the live agent state, and persist
  // per case — exactly the findings_summary pattern above.
  useEffect(() => {
    if (!agent.review_report_json || !activeThread?.id) return
    useReviewStore.getState().setFromJson(agent.review_report_json)
    localStorage.setItem(`oio-review-${activeThread.id}`, agent.review_report_json)
  }, [agent.review_report_json, activeThread?.id])

  // Reconnect / fresh-open recovery for the REVIEW LAYER from its GCS sidecar
  // (reviewReportBlobUrl in thread metadata) — mirrors the findings recovery.
  useEffect(() => {
    const id = activeThread?.id
    if (!id || draftStatus !== "draft_completed") return
    if (localStorage.getItem(`oio-review-${id}`)) return
    const meta = (activeThread?.metadata ?? {}) as Record<string, unknown>
    const blobUrl =
      (typeof meta.reviewReportBlobUrl === "string" && meta.reviewReportBlobUrl) ||
      (typeof meta.review_report_blob_url === "string" && meta.review_report_blob_url) ||
      ""
    if (!blobUrl) return
    let cancelled = false
    readDraftText(blobUrl)
      .then((text) => {
        if (!text || cancelled) return
        useReviewStore.getState().setFromJson(text)
        localStorage.setItem(`oio-review-${id}`, text)
      })
      .catch((err) => console.error("[OIOWorkspace] review report blob read failed:", err))
    return () => {
      cancelled = true
    }
  }, [activeThread?.id, draftStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reconnect / fresh-open recovery for the ANALYSIS: a completed case opened
  // with no cached findings and no live stream (reopened after close/sleep,
  // opened on another device, or where the reconnect poller flipped status
  // before the stream delivered findings) restores the allegation-wise analysis
  // + demand from the recoverable findings blob. Mirrors the draft recovery.
  useEffect(() => {
    const id = activeThread?.id
    if (!id || draftStatus !== "draft_completed") return
    if (localStorage.getItem(`oio-findings-${id}`)) return // already cached / stream-loaded
    const meta = (activeThread?.metadata ?? {}) as Record<string, unknown>
    const blobUrl =
      (typeof meta.findingsBlobUrl === "string" && meta.findingsBlobUrl) ||
      (typeof meta.findings_blob_url === "string" && meta.findings_blob_url) ||
      ""
    if (!blobUrl) return
    let cancelled = false
    readDraftText(blobUrl)
      .then((text) => {
        if (!text || cancelled) return
        const findings = JSON.parse(text) as FindingsSummaryItem[]
        if (!Array.isArray(findings) || findings.length === 0) return
        analysisStore.setAnalysisData({ findings_summary: findingsToAllegations(findings) })
        localStorage.setItem(`oio-findings-${id}`, text)
      })
      .catch((err) => console.error("[OIOWorkspace] findings blob read failed:", err))
    return () => {
      cancelled = true
    }
  }, [activeThread?.id, draftStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Early-populate the Analysis tab from the aligned allegations (published at
  // the alignment boundary, before the analysis fan-out) so officers see the
  // real demands under review while analysis runs. Skipped once full findings
  // (richer: determinations + demand) are available so we never downgrade them.
  useEffect(() => {
    if (!activeThread?.id || agent.findings_summary_json) return
    const parsed = parseAlignedAllegations(agent.aligned_allegations_json)
    if (parsed.length === 0) return
    const existing = analysisStore.allegations
    if (existing.some((a) => a.status !== "unknown")) return // keep richer data
    analysisStore.setAnalysisData({ findings_summary: alignedToAllegations(parsed) })
    localStorage.setItem(
      `oio-allegs-${activeThread.id}`,
      JSON.stringify(agent.aligned_allegations_json),
    )
  }, [agent.aligned_allegations_json, agent.findings_summary_json, activeThread?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track latest artifact_blob_url; load the draft from storage if localStorage
  // is empty. The artifact is PRIVATE in GCS, so we read it through the
  // authenticated backend proxy (a direct fetch 403s).
  useEffect(() => {
    if (!agent.artifact_blob_url || !activeThread?.id) return
    if (activeThread?.metadata?.artifact_blob_url === agent.artifact_blob_url) return

    blobUrlRef.current = agent.artifact_blob_url
    setActiveThread({
      ...activeThread!,
      metadata: {
        ...(activeThread!.metadata ?? {}),
        artifact_blob_url: agent.artifact_blob_url,
      },
    })
  }, [agent.artifact_blob_url, activeThread?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof agent.draft_revision !== "number") return
    if (agent.draft_revision <= orderDraftStore.baseRevision) return
    orderDraftStore.setBaseRevision(agent.draft_revision)
  }, [agent.draft_revision, orderDraftStore]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the same artifact tracking for the rewrite agent. After persist_commit
  // completes, the rewrite STATE_SNAPSHOT carries artifact_blob_url and draft_revision.
  // This effect updates the thread metadata so DocumentViewer reloads the committed draft.
  const rewriteBlobUrl = rewriteAgent.artifact_blob_url
  useEffect(() => {
    if (!rewriteBlobUrl || !activeThread?.id) return
    if (activeThread?.metadata?.artifact_blob_url === rewriteBlobUrl) return
    blobUrlRef.current = rewriteBlobUrl
    setActiveThread({
      ...activeThread!,
      metadata: {
        ...(activeThread!.metadata ?? {}),
        artifact_blob_url: rewriteBlobUrl,
      },
    })
  }, [rewriteBlobUrl, activeThread?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const rewriteDraftRevision = rewriteAgent.draft_revision
  useEffect(() => {
    if (typeof rewriteDraftRevision !== "number") return
    if (rewriteDraftRevision <= orderDraftStore.baseRevision) return
    orderDraftStore.setBaseRevision(rewriteDraftRevision)
  }, [rewriteDraftRevision, orderDraftStore]) // eslint-disable-line react-hooks/exhaustive-deps

  // After approval, persist_commit emits updated_oio_draft + changed_ranges in
  // the SAME STATE_SNAPSHOT. Applying them in one effect (rather than two
  // separate effects each with their own setState) lets React 18 batch both
  // updates into a single re-render — DocumentRenderer's ReactMarkdown then
  // re-parses the (multi-thousand-line) document once instead of twice in
  // quick succession, which was blocking the main thread long enough for the
  // tab to appear unresponsive right after a commit. Write updated_oio_draft
  // directly to orderDraftStore for immediate canvas update — the GCS fetch
  // via artifact_blob_url would also re-render (blobUrlRef changes) but this
  // avoids the round-trip latency on the approve path. changed_ranges always
  // wins over the cold-load fetch below.
  const rewriteUpdatedDraft = rewriteAgent.updated_oio_draft
  const rewriteChangedRanges = rewriteAgent.changed_ranges
  useEffect(() => {
    if (rewriteUpdatedDraft && rewriteUpdatedDraft !== orderDraftStore.content) {
      orderDraftStore.setContent(rewriteUpdatedDraft)
    }
    if (rewriteChangedRanges) {
      setChangedRanges(rewriteChangedRanges)
    }
  }, [rewriteUpdatedDraft, rewriteChangedRanges]) // eslint-disable-line react-hooks/exhaustive-deps
  const { appendMessage } = useCopilotChatInternal()

  const handleSelectionAction = useCallback(async (userMessage: string) => {
    if (!userMessage.trim()) return
    // Clear selection display
    setSelectionRect(null)
    setSelectedText("")
    setIsToolbarExpanded(false)
    window.getSelection()?.removeAllRanges()
    // Switch to the chat tab so the user can see the message and the loader
    setLeftTab("chat")
    if (activeThread?.id) {
      // artifact_blob_url presence is the canonical signal that a draft exists
      // and the officer is in rewrite mode — avoids status-flicker conditions.
      const isRewriteMode = Boolean(activeThread?.metadata?.artifact_blob_url)

      if (isRewriteMode) {
        void runRewrite(userMessage)
        return
      } else {
        await markNewAttempt("oio", activeThread.id)
      }
    }
    // Send the message — appendMessage fires the stream
    void appendMessage(new TextMessage({ role: Role.User, content: userMessage }))
  }, [activeThread?.id, activeThread?.metadata?.artifact_blob_url, appendMessage, runRewrite, setLeftTab, setIsToolbarExpanded])

  // Track selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      if (isToolbarExpanded) return // Keep open when mini-composer is expanded
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !docPaneRef.current) {
        setSelectionRect(null)
        setSelectedText("")
        return
      }

      try {
        const range = sel.getRangeAt(0)
        const within = range.commonAncestorContainer
        const inEditor = (within.nodeType === 1 ? (within as Element) : within.parentElement)?.closest("[data-doc-editing]")
        // A selection inside an open paragraph editor is the officer editing
        // (select a word, type over it) — not a passage to ask about.
        if (!docPaneRef.current.contains(within) || inEditor) {
          setSelectionRect(null)
          setSelectedText("")
          return
        }

        const rects = range.getClientRects()
        if (rects.length === 0) {
          setSelectionRect(null)
          setSelectedText("")
          return
        }

        const docRect = docPaneRef.current.getBoundingClientRect()
        const rangeRect = range.getBoundingClientRect()

        const top = rangeRect.top - docRect.top + docPaneRef.current.scrollTop
        const left = rangeRect.left - docRect.left + docPaneRef.current.scrollLeft + (rangeRect.width / 2)

        setSelectionRect({ top, left })
        setSelectedText(sel.toString().trim())
      } catch (e) {
        setSelectionRect(null)
        setSelectedText("")
      }
    }

    document.addEventListener("selectionchange", handleSelectionChange)
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange)
    }
  }, [isToolbarExpanded])

  // Track rewrite status to trigger flash-highlight animation
  useEffect(() => {
    if (prevRewriteStatus.current === "running" && rewriteStatus === "done") {
      setJustUpdated(true)
      const timer = setTimeout(() => {
        setJustUpdated(false)
      }, 2200)
      return () => clearTimeout(timer)
    }
    prevRewriteStatus.current = rewriteStatus
  }, [rewriteStatus])

  // The per-case generation store knows this case is running even while
  // CopilotKit reconnects (after a case switch / page change), so we don't
  // bounce back to the upload page during the reconnection gap.
  const runs = useGenerationStore((s) => s.runs)
  const run = runFor(runs, activeThread?.id)
  const genRunningHere = run?.status === "running"
  const isGenerating =
    draftStatus === "draft_starting" || draftStatus === "draft_running" || genRunningHere

  useRailWhileGenerating(isGenerating)

  // THE ORDER IS EDITABLE WHERE IT STANDS: click a paragraph, type, click away
  // (features/workspace/components/EditableParagraph.tsx). An adjudication
  // draft is the officer's own once delivered, so a save is the draft written
  // back to its blob — the path `artifact_blob_url` was always documented for
  // (useOIOAgent.ts). Not while a draft or a rewrite is writing the same blob,
  // and not in mock replay (mock:// artifacts are not storage).
  const draftBlobUrl =
    (activeThread?.metadata?.artifact_blob_url as string | undefined) ??
    (activeThread?.metadata?.artifactBlobUrl as string | undefined)
  const editQueue = useRef<Promise<unknown>>(Promise.resolve())
  const commitEdit = useCallback(
    (edit: DocumentEdit) => {
      const run = async (): Promise<string[] | null> => {
        if (!draftBlobUrl) return ["This draft has no saved copy to write to yet."]
        const next = applyDocumentEdit(useOrderDraftStore.getState().content, edit)
        if (next === null) return ["The order changed while you were typing. Make the change again."]
        try {
          await overwriteDraftText(draftBlobUrl, next)
        } catch (error) {
          return [error instanceof Error && error.message ? error.message : "The edit could not be saved."]
        }
        // The last rewrite's highlights are LINE ranges. A reworded paragraph
        // is still one line, so they stay true; only an edit that adds or
        // removes lines would leave them pointing at the wrong blocks.
        if (edit.source.split("\n").length !== edit.markdown.split("\n").length) setChangedRanges(null)
        useOrderDraftStore.getState().setContent(next)
        useOrderDraftStore.getState().setSaved(true)
        return null
      }
      const result = editQueue.current.then(run, run)
      editQueue.current = result.catch(() => undefined)
      return result
    },
    [draftBlobUrl],
  )
  const docEditing = useMemo<DocumentEditing | undefined>(
    () =>
      draftBlobUrl && !draftBlobUrl.startsWith("mock://") && !isMockReplay()
        ? { onCommit: commitEdit, disabled: isGenerating || rewriteRunning }
        : undefined,
    [draftBlobUrl, commitEdit, isGenerating, rewriteRunning],
  )

  const handleGenerateDraft = (scnUrl: string, replyUrl: string | null, supportingUrls: string[], supportingFileNames: string[] = [], submissionId?: string) => {
    if (draftStatus !== "draft_not_started") return
    const prompt = buildPrompt(scnUrl, replyUrl, supportingUrls, scnFileName ?? "", replyFileName ?? "", supportingFileNames)
    setLeftTab("chat")
    void (async () => {
      await start(prompt)
      if (submissionId && activeThread?.id && session?.user.id) acknowledgeSimulatorHandoff(session.user.id, activeThread.id, submissionId)
    })().catch(() => {})
  }

  useEffect(() => {
    const caseId = activeThread?.id
    const userId = session?.user.id
    if (!caseId || !userId || draftStatus !== "draft_not_started") return
    const handoff = readSimulatorHandoff(userId, caseId, "oio")
    if (!handoff || !claimSimulatorHandoff(userId, caseId, handoff.submissionId)) return
    const input = handoff.prepared as PreparedOioInput
    handleGenerateDraft(input.scn.url, input.reply?.url ?? null, input.supporting.map((file) => file.url), input.supporting.map((file) => file.filename), handoff.submissionId)
  }, [activeThread?.id, draftStatus, session?.user.id])

  // ── Retry: re-run generation from the already-uploaded docs after a failure ─
  const setRetryHandler = useRetryStore((s) => s.setHandler)
  const retryGeneration = useCallback(async () => {
    if (!activeThread?.id) return
    const meta = (activeThread.metadata ?? {}) as Record<string, unknown>
    const scnUrl = meta.scnFileUrl as string | undefined
    const replyUrl = (meta.replyFileUrl as string | undefined) ?? null
    // A missing reply URL is only valid when the officer declared the case
    // ex-parte — for anything else (legacy cases, interrupted uploads) keep
    // the old bail-out rather than silently retrying without the defence.
    if (!scnUrl || (!replyUrl && meta.replyStatus !== "not_received")) return
    // Clear any prior fatal error so the banner + failed panel reset on retry.
    useErrorStore.getState().clearAll()
    const supportingUrls = Array.isArray(meta.supportingFiles)
      ? (meta.supportingFiles as Array<{ url: string }>).map((f) => f.url)
      : []
    // Reset the failed status locally so the pipeline is allowed to run again.
    setActiveThread({
      ...activeThread,
      metadata: { ...meta, draft_status: "draft_not_started" },
    })
    await refreshThreads()
    const prompt = buildPrompt(
      scnUrl,
      replyUrl,
      supportingUrls,
      scnFileName ?? "",
      replyFileName ?? "",
    )
    setLeftTab("chat")
    void start(prompt).catch(() => {})
  }, [activeThread, scnFileName, replyFileName, start, setActiveThread, refreshThreads])

  useEffect(() => {
    setRetryHandler(retryGeneration)
    return () => setRetryHandler(null)
  }, [retryGeneration, setRetryHandler])

  const draftContent = orderDraftStore.content
  const hasDraft = Boolean(draftContent)
  const isFailed = draftStatus === "draft_failed_terminal" && !hasDraft && !isGenerating

  // ── Completion summary card (chat) ──────────────────────────────────────
  // Derived from state on every render — never sent through CopilotKit, never
  // stored. Prefer the live agent findings; fall back to the per-case cache
  // this component already persists (that's what survives refresh, since
  // useCoAgent state resets on remount). analysisStore.allegations is a dep
  // purely as a recompute trigger: the blob-recovery effect above writes the
  // cache and the store in the same flow.
  const reviewReport = useReviewStore((s) => s.report)
  const summaryFindings = useMemo(() => {
    const cached = activeThread?.id ? localStorage.getItem(`oio-findings-${activeThread.id}`) : null
    return parseFindingsSummary(agent.findings_summary_json ?? cached)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.findings_summary_json, activeThread?.id, analysisStore.allegations])
  const summaryRevision =
    agent.draft_revision ?? (orderDraftStore.baseRevision > 0 ? orderDraftStore.baseRevision : null)
  // One card per completed draft: a single derived node, keyed on
  // thread_id + draft_revision (re-renders can never append duplicates).
  const summaryCard =
    draftStatus === "draft_completed" && hasDraft && summaryFindings.length > 0 && activeThread?.id ? (
      <OrderSummaryCard
        key={`${activeThread.id}:${summaryRevision ?? 0}`}
        threadId={activeThread.id}
        draftRevision={summaryRevision}
        findings={summaryFindings}
        reviewReport={reviewReport}
      />
    ) : undefined

  const flowEmpty = !hasDraft && !isGenerating && draftStatus === "draft_not_started"
  // Marked completed but the draft artifact is gone (e.g. an interrupted run, or
  // a draft saved before this build). Show a clear recover state rather than the
  // contradictory "Ready for review" + "No draft yet".
  const draftMissing = draftStatus === "draft_completed" && !hasDraft && !isGenerating

  // The officer explicitly marks the order filed once it's finalised. Section-
  // by-section approval was removed — the full OIO is loaded for review, and a
  // CopilotKit HITL rewrite flow will handle edits/refinement later.
  const isFiled = (activeThread?.metadata as Record<string, unknown> | undefined)?.draft_status === "draft_filed"
  const [filingBusy, setFilingBusy] = useState(false)
  const toggleFiled = useCallback(async () => {
    // MOCK REPLAY: filing persists to the backend — quietly disabled (the
    // button below is also disabled with a tooltip).
    if (isMockReplay()) return
    if (!activeThread?.id) return
    const meta = (activeThread.metadata ?? {}) as Record<string, unknown>
    const next = meta.draft_status === "draft_filed" ? "draft_completed" : "draft_filed"
    setFilingBusy(true)
    try {
      const updated = await casesService.update(activeThread.id, {
        metadata: { ...meta, draft_status: next },
      })
      setActiveThread(updated)
      await refreshThreads()
    } catch (err) {
      console.error("[OIOWorkspace] toggle-filed failed:", err)
    } finally {
      setFilingBusy(false)
    }
  }, [activeThread, setActiveThread, refreshThreads])

  // NEXT STEPS — what the checks on this order found, as things the officer can
  // act on (lib/next-steps.ts). The order is always delivered now; a finding
  // that used to WITHHOLD it is a step he settles before it is signed. His
  // marks are his own, kept on the case (thread metadata), not engine state.
  const nextSteps = useMemo(() => buildOioNextSteps(reviewReport), [reviewReport])
  const stepDecisions = useMemo(
    () => readDecisions(activeThread?.metadata as Record<string, unknown> | undefined),
    [activeThread?.metadata],
  )
  const openStepCount = openSteps(nextSteps, stepDecisions).length
  const mustSettle = mustSettleCount(nextSteps, stepDecisions)
  const heldReason =
    mustSettle > 0
      ? `Not cleared for signature: ${mustSettle === 1 ? "one next step has" : `${mustSettle} next steps have`} to be settled first. Open Next steps.`
      : undefined

  const decideStep = useCallback(
    async (step: OioNextStep, decision: OioStepDecision | null) => {
      if (isMockReplay() || !activeThread?.id) return
      const meta = (activeThread.metadata ?? {}) as Record<string, unknown>
      const marks = { ...readDecisions(meta) } as Record<string, OioStepDecision>
      if (decision) marks[step.id] = decision
      else delete marks[step.id]
      try {
        setActiveThread(await casesService.update(activeThread.id, { metadata: { ...meta, oioNextSteps: marks } }))
      } catch (err) {
        console.error("[OIOWorkspace] next-step mark failed:", err)
      }
    },
    [activeThread, setActiveThread],
  )

  // A delivered order with something to settle opens on Next steps — once per
  // case, so the officer's own choice of tab is never taken back.
  const nextTabOpenedFor = useRef<string | null>(null)
  useEffect(() => {
    const id = activeThread?.id ?? null
    if (!id || mustSettle === 0 || nextTabOpenedFor.current === id) return
    nextTabOpenedFor.current = id
    setLeftTab("next")
  }, [activeThread?.id, mustSettle])

  // Whether this case runs ex-parte (no reply to the SCN was ever filed).
  // Prefer the live agent state (reply_status is recorded at ingest — the
  // backend's authoritative determination); fall back to the officer's
  // declaration persisted at upload time (replyStatus), which is what survives
  // reloads and reconnects since agent state resets on remount. '' means a
  // legacy checkpoint from before the reply became optional.
  const replyStatus =
    agent.reply_status ||
    (activeThread?.metadata?.replyStatus as string | undefined) ||
    null
  const isExParte = replyStatus === "not_received" && !replyFileName

  // Supporting documents (hearing notices, correspondence, etc.) are uploaded
  // alongside the SCN + reply and stored on the thread — surface them as chips
  // too, not just the SCN and reply. The reply slot renders the ex-parte
  // status chip when no reply exists.
  const supportingFiles = Array.isArray(activeThread?.metadata?.supportingFiles)
    ? (activeThread.metadata.supportingFiles as Array<{ name?: string; url?: string }>)
    : []
  const fileChips = [
    scnFileName
      ? { kind: "doc" as const, name: scnFileName, url: scnFileUrl, label: "Show Cause Notice" }
      : null,
    replyFileName
      ? { kind: "doc" as const, name: replyFileName, url: replyFileUrl, label: "Taxpayer Reply" }
      : isExParte
        ? { kind: "status" as const, name: "No reply filed (ex-parte)", label: "Taxpayer Reply" }
        : null,
    ...supportingFiles.map((f, i) => ({
      kind: "doc" as const,
      name: f.name || `Supporting document ${i + 1}`,
      url: f.url,
      label: "Supporting document",
    })),
  ].filter(Boolean) as Array<{ kind: "doc" | "status"; name: string; url?: string; label: string }>

  // ── Dedicated upload page (no draft yet) ────────────────────────────────
  if (flowEmpty) {
    return (
      <div className="uploadpage">
        <div className="uploadpage__inner">
          <div className="uploadpage__card">
            <div className="uphead">
              <h3>New Order-in-Original</h3>
              <p>
                Upload the Show Cause Notice, and the taxpayer's reply if one was filed.
                NitiAssist AI will analyse the case record and draft the complete
                Order-in-Original for your review — ex-parte when no reply exists.
              </p>
            </div>
            {isMockReplay() ? (
              // MOCK REPLAY: the case arrives pre-seeded and Generate starts
              // the scripted replay instead of the real drafting run.
              <Suspense fallback={null}>
                <MockSeededUploader />
              </Suspense>
            ) : (
              <CaseFileUploader
                onGenerateDraft={handleGenerateDraft}
                isGenerating={isGenerating}
                hasDraftSessionStarted={draftStatus !== "draft_not_started"}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="oio relative">
      {hitlProposal && (
        <ApprovalPanel
          status="awaiting_approval"
          proposalSummary={hitlProposal.summary}
          diffText={hitlProposal.diff}
          onApprove={hitlProposal.onApprove}
          onReject={hitlProposal.onReject}
          onEditApprove={hitlProposal.onEditApprove}
          onSteer={hitlProposal.onSteer}
          onCancel={hitlProposal.onCancel}
          possibleRetryAfterFailure={hitlProposal.possibleRetryAfterFailure}
        />
      )}

      <div
        className="oio__split"
        ref={splitRef}
        style={leftWidth ? { gridTemplateColumns: `${leftWidth}px 6px 1fr` } : undefined}
      >
        {/* LEFT: context */}
        <div className="oio__left">
          <div className="tabs" role="tablist">
            <button
              role="tab"
              aria-selected={leftTab === "chat"}
              className={"tab" + (leftTab === "chat" ? " is-active" : "")}
              onClick={() => setLeftTab("chat")}
            >
              <Icon name="message" size={15} /> Chat
            </button>
            {(nextSteps.length > 0 || leftTab === "next") && (
              <button
                role="tab"
                aria-selected={leftTab === "next"}
                className={"tab" + (leftTab === "next" ? " is-active" : "")}
                onClick={() => setLeftTab("next")}
                data-testid="oio-tab-next"
              >
                <Icon name="flag" size={15} /> Next steps
                {openStepCount > 0 && (
                  <span className={"oia-tab-count" + (mustSettle > 0 ? " oia-tab-count--hot" : "")}>{openStepCount}</span>
                )}
              </button>
            )}
            <button
              role="tab"
              aria-selected={leftTab === "analysis"}
              className={"tab" + (leftTab === "analysis" ? " is-active" : "")}
              onClick={() => setLeftTab("analysis")}
            >
              <Icon name="chart" size={15} /> Analysis
            </button>
          </div>

          {leftTab === "chat" ? (
            <div className="chat">
              {fileChips.length > 0 && (
                <div className="chat__docs">
                  {fileChips.map((f) =>
                    f.kind === "status" ? (
                      <span
                        key={f.name}
                        className="chip"
                        style={{ cursor: "default" }}
                        title="No reply to the Show Cause Notice was filed — the order proceeds ex-parte on the record"
                        data-testid="ex-parte-chip"
                      >
                        <Icon name="alert" size={12} /> {f.name}
                      </span>
                    ) : isMockReplay() ? (
                      // MOCK REPLAY: the document viewer fetches the blob from
                      // storage — render the chip inert instead.
                      <span
                        key={f.name}
                        className="chip chip--ok"
                        style={{ cursor: "default" }}
                        title="Document preview is disabled in mock replay"
                      >
                        <Icon name="file" size={12} /> {f.name}
                      </span>
                    ) : (
                      <button
                        key={f.name}
                        className="chip chip--ok"
                        title="View document"
                        onClick={() => setViewFile(f)}
                      >
                        <Icon name="file" size={12} /> {f.name}
                      </button>
                    ),
                  )}
                </div>
              )}
              <div className="oio-chat-host">
                <div style={{ padding: "0 8px" }}>
                  {showRejectFollowup && rewriteStatus !== "running" && (
                    <div className="chip chip--warn" style={{ margin: "8px 0", display: "flex", alignItems: "center", gap: 6 }}>
                      <span>↩</span>
                      <span>Proposal rejected — type your next instruction below to guide the agent.</span>
                      <button
                        onClick={() => setShowRejectFollowup(false)}
                        style={{ marginLeft: "auto", opacity: 0.6, cursor: "pointer", background: "none", border: "none", fontSize: 14 }}
                        aria-label="Dismiss"
                      >✕</button>
                    </div>
                  )}
                </div>
                {draftStatus === "draft_completed" ? (
                  <RewriteChatPanel
                    checkpointMessages={rewriteAgent.messages}
                    isLoading={rewriteRunning}
                    hasDraft={hasDraft}
                    draftStatus={draftStatus}
                    summaryCard={summaryCard}
                    onSend={(content) => {
                      void runRewrite(content)
                    }}
                    onAttachEvidence={handleAttachEvidence}
                    attachBusy={evidenceUploading}
                    hitlNode={hitlNode}
                  />
                ) : (
                  <DraftGenerationPane hitlNode={hitlNode} />
                )}
              </div>
            </div>
          ) : leftTab === "next" ? (
            <OioNextStepsPanel
              steps={nextSteps}
              decisions={stepDecisions}
              busy={isGenerating || rewriteRunning || isMockReplay()}
              onShow={(step) => showSection(step.section, docPaneRef.current)}
              onFix={(step) => handleSelectionAction(fixInstruction(step))}
              onOpenAnalysis={() => setLeftTab("analysis")}
              onDecide={(step, decision) => void decideStep(step, decision)}
            />
          ) : (
            <AnalysisPane generating={isGenerating} hasDraft={hasDraft} />
          )}
        </div>

        {/* Draggable divider */}
        <div
          className="oio__resizer"
          onMouseDown={startResize}
          onDoubleClick={() => {
            localStorage.removeItem("oio-split-left")
            setLeftWidth(null)
          }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels"
          title="Drag to resize · double-click to reset"
        />

        {/* RIGHT: draft */}
        <div className="oio__right">
          <div className="draft-bar">
            <Icon name="file" size={17} />
            <span className="draft-bar__title">Order Draft</span>
            {scnFileName && <span className="draft-bar__meta">— {scnFileName}</span>}
            {isExParte && (
              <span
                className="pill pill--neutral"
                data-testid="reply-status-pill"
                title="No reply to the Show Cause Notice was filed — the order proceeds ex-parte on the record"
              >
                Ex-parte — no reply filed
              </span>
            )}
            <div style={{ flex: 1 }} />
            {hasDraft && (
              <>
                <button
                  className={"btn btn--sm " + (isFiled ? "btn--filed" : "btn--ghost")}
                  onClick={toggleFiled}
                  disabled={filingBusy || isMockReplay() || (Boolean(heldReason) && !isFiled)}
                  title={
                    isMockReplay()
                      ? "Disabled in mock replay"
                      : heldReason && !isFiled
                        ? heldReason
                        : isFiled
                        ? "This order is filed — click to unmark"
                        : "Mark this order as filed"
                  }
                >
                  {filingBusy ? (
                    <span className="spinner" style={{ width: 13, height: 13 }} />
                  ) : (
                    <Icon name={isFiled ? "checkc" : "flag"} size={15} />
                  )}
                  {isFiled ? "Filed" : "Mark as filed"}
                </button>
                <ShareMenu
                  data={draftContent}
                  fileName={scnFileName ? `OIO_${scnFileName.replace(/\.[^.]+$/, "")}` : "OIO_Draft"}
                  heldReason={heldReason}
                />
              </>
            )}
          </div>

          {rewriteStatus === "running" && (
            <div className="rewrite-progress-banner rewrite-progress-banner--overlay" data-testid="rewrite-progress-banner">
              <span className="spinner" style={{ width: 14, height: 14 }} />
              <span>{isApplying ? "Applying your edit…" : currentStage || "Revising…"}</span>
            </div>
          )}

          {isGenerating ? (
            <ProgressView
              startedAt={run?.startedAt}
              agent={{
                // Merge live agent state with the persisted store so completed
                // steps survive a reconnection (the agent state resets to empty
                // on remount until the run emits its next snapshot).
                completed_stages: Array.from(
                  new Set([
                    ...(agent.completed_stages ?? []),
                    ...(run?.completedStages ?? []),
                  ]),
                ),
                current_stage: agent.current_stage ?? run?.currentStage ?? null,
                current_substage: agent.current_substage ?? run?.currentSubstage ?? null,
              }}
              alignedAllegationsJson={agent.aligned_allegations_json}
              findingsSummaryJson={agent.findings_summary_json}
              replyStatus={replyStatus}
            />
          ) : isFailed ? (
            <FailedDraftState
              onRetry={retryGeneration}
              agent={agent}
              errorDetail={activeThread?.metadata?.error_detail as string | undefined}
            />
          ) : draftMissing ? (
            <div className="draft-scroll">
              <div className="prog__panel">
                <div className="failbox" role="alert">
                  <div className="failbox__icon">
                    <Icon name="alert" size={22} />
                  </div>
                  <h3>Draft not available</h3>
                  <p>
                    This case is marked complete, but its order draft isn't available on this
                    device. Regenerate it from the uploaded documents.
                  </p>
                  <div className="failbox__actions">
                    <button className="btn btn--primary" onClick={retryGeneration}>
                      <Icon name="retry" size={15} /> Regenerate draft
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : hasDraft ? (
            <div className="draft-scroll" data-testid="order-draft-content" ref={docPaneRef} style={{ position: "relative" }}>
              {selectionRect && (
                <SelectionToolbar
                  top={selectionRect.top}
                  left={selectionRect.left}
                  selectedText={selectedText}
                  onSubmit={handleSelectionAction}
                  onExpandedChange={setIsToolbarExpanded}
                />
              )}
              <div className={`page oio-doc${justUpdated ? " oio-doc--just-updated" : ""}`} data-testid="oio-doc-pane">
                <DocumentRenderer content={draftContent} changedRanges={changedRanges ?? undefined} editing={docEditing} />
              </div>
            </div>
          ) : (
            <div className="empty" style={{ height: "100%" }}>
              <div className="empty__ico">
                <Icon name="file" size={24} />
              </div>
              <h3>No draft yet</h3>
              <p>
                Upload the Show Cause Notice — and the Taxpayer Reply, if one was filed — then
                generate the Order-in-Original.
              </p>
            </div>
          )}
        </div>
      </div>

      {viewFile && <DocViewerModal file={viewFile} onClose={() => setViewFile(null)} />}
    </div>
  )
}

// ── Progress (long-running, real-stage-driven checklist) ──────────────────
interface OIOAgentLike {
  completed_stages?: string[]
  current_stage?: string | null
  current_substage?: string | null
}

function fmtElapsed(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`
}

function ProgressView({
  agent,
  startedAt,
  alignedAllegationsJson,
  findingsSummaryJson,
  replyStatus,
}: {
  agent: OIOAgentLike
  startedAt?: number
  alignedAllegationsJson?: string[] | null
  findingsSummaryJson?: string | null
  replyStatus?: string | null
}) {
  // Elapsed is computed from the persisted run start time (store + localStorage),
  // so it reflects the real duration across case/tab switches and reloads rather
  // than restarting from 0 on every remount.
  const anchor = startedAt ?? Date.now()
  const [elapsed, setElapsed] = useState(() => Math.max(0, (Date.now() - anchor) / 1000))
  useEffect(() => {
    const id = setInterval(() => setElapsed(Math.max(0, (Date.now() - anchor) / 1000)), 1000)
    return () => clearInterval(id)
  }, [anchor])

  const completed = new Set(agent.completed_stages ?? [])
  const current = agent.current_stage ?? null
  // Mirror the left transcript (GenerationThinking): when `current_stage` is
  // absent — mid-node emits don't propagate, so it usually is — the first
  // not-yet-completed stage is the running one. Keeps both panes in sync.
  const activeStage = deriveActiveStage(current, agent.completed_stages ?? undefined)
  const reachedDrafting =
    completed.has("section_draft") || current === "section_draft" || current === "compile"

  return (
    <div className="draft-scroll" data-testid="oio-activity-feed">
      <div className="prog__panel">
        <div className="stages" role="status" aria-live="polite">
          <div className="stages__head">
            <div>
              <h3>Drafting the Order-in-Original</h3>
              <div className="stages__est">
                A full adjudication typically takes 8–12 minutes — follow the live reasoning in
                the chat on the left.
              </div>
            </div>
            <div className="stages__timer">
              <Icon name="clock" size={14} /> {fmtElapsed(elapsed)}
            </div>
          </div>
          {OIO_STAGES.map((s) => {
            const done = completed.has(s.id)
            const active = activeStage === s.id && !done
            return (
              <div
                key={s.id}
                className={"stage" + (done ? " is-done" : active ? " is-active" : "")}
              >
                <div className="stage__icon">
                  {done ? (
                    <Icon className="tick" name="checkc" size={18} />
                  ) : active ? (
                    <span className="spinner" />
                  ) : (
                    <span className="stage__ring" />
                  )}
                </div>
                <div>
                  {/* The right-side tracker stays high-level: stage title only,
                      marked done/running. The granular live activity lives in the
                      left transcript (GenerationThinking). */}
                  <div className="stage__title">{s.label}</div>
                </div>
                <div className="stage__time">{done ? "done" : active ? "running" : ""}</div>
              </div>
            )
          })}
        </div>
        {/* "From the record so far": real case content, filled progressively as
            node boundaries flush state (alignment → issues; speaking_order →
            provisional stance). The substage rotation above stays the header
            line of the current stage; this feed sits under the checklist. */}
        <LiveFindingsFeed
          alignedAllegationsJson={alignedAllegationsJson}
          findingsSummaryJson={findingsSummaryJson}
          replyStatus={replyStatus}
        />
        {reachedDrafting && (
          <div style={{ marginTop: 18 }}>
            <SkeletonPage />
          </div>
        )}
      </div>
    </div>
  )
}

function SkeletonPage() {
  return (
    <div className="skel-page" aria-hidden="true">
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <div className="skel" style={{ width: 160, height: 9, margin: "0 auto 12px" }} />
        <div className="skel" style={{ width: 300, height: 16, margin: "0 auto" }} />
      </div>
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="skel"
          style={{ width: i % 3 === 0 ? "92%" : i % 3 === 1 ? "100%" : "78%" }}
        />
      ))}
    </div>
  )
}

// ── Failed state (safe fallback: status + retry + report) ─────────────────
function FailedDraftState({
  onRetry, agent, errorDetail,
}: { onRetry: () => void; agent: OIOAgentLike; errorDetail?: string }) {
  const [reported, setReported] = useState(false)
  const completed = new Set(agent.completed_stages ?? [])
  const stoppedAt = OIO_STAGES.find((s) => !completed.has(s.id))

  const report = () => {
    const fatal = useErrorStore.getState().fatalError()
    void ReportIssueClient.report(
      fatal ??
        ({
          code: "DRAFT_FAILED",
          userMessage: "Draft generation was interrupted before the OIO could be completed.",
          severity: "fatal",
          category: "unknown",
          retryable: true,
        } as never),
    )
    setReported(true)
  }

  return (
    <div className="draft-scroll">
      <div className="prog__panel">
        <div className="failbox" role="alert">
          <div className="failbox__icon">
            <Icon name="alert" size={22} />
          </div>
          {/* "Needs attention" must stay in sync with case-view.ts's
              deriveStatus mapping for draft_failed_terminal — that's the
              single canonical source of officer-facing status copy; this
              pill is the only place draft_failed_terminal is shown inside
              the workspace itself (case-view.ts's own copy only renders in
              case-list views: Nav/Home/Vault). */}
          <span className="pill pill--upheld" data-testid="case-status-pill">
            Needs attention
          </span>
          <h3>Draft generation was interrupted</h3>
          <p>
            {stoppedAt
              ? `The order stopped while ${stoppedAt.label.toLowerCase()}.`
              : "The order could not be completed."}{" "}
            Your uploaded documents are saved — you can retry, or report the issue and we'll look
            into it.
          </p>
          {errorDetail && (
            <p className="failbox__detail" data-testid="draft-error-detail">
              {errorDetail}
            </p>
          )}
          <div className="failbox__actions">
            <button className="btn btn--primary" onClick={onRetry}>
              <Icon name="retry" size={15} /> Retry generation
            </button>
            <button className="btn btn--ghost" onClick={report} disabled={reported}>
              <Icon name={reported ? "check" : "shield"} size={15} />
              {reported ? "Issue reported" : "Report issue"}
            </button>
          </div>

          <div className="failbox__steps">
            {OIO_STAGES.map((s) => {
              const done = completed.has(s.id)
              const failed = stoppedAt?.id === s.id
              return (
                <div
                  key={s.id}
                  className={"failstep" + (done ? " is-done" : failed ? " is-failed" : "")}
                >
                  <span className="failstep__ico">
                    {done ? (
                      <Icon className="tick" name="checkc" size={15} />
                    ) : failed ? (
                      <Icon name="alert" size={14} />
                    ) : (
                      <span className="stage__ring" />
                    )}
                  </span>
                  {s.label}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Analysis pane (allegation-wise findings from analysisStore) ───────────
const STATUS_TO_PILL: Record<Allegation["status"], { cls: string; label: string }> = {
  confirmed: { cls: "upheld", label: "Demand upheld" },
  partial: { cls: "partial", label: "Partly upheld" },
  dropped: { cls: "favour", label: "Dropped" },
  unknown: { cls: "neutral", label: "Under review" },
}

function formatAmount(amount?: number): string {
  if (amount == null) return "—"
  return "₹ " + amount.toLocaleString("en-IN")
}

const ANALYSIS_ROW_CAP = 60

const AnalysisPane = memo(function AnalysisPane({ generating, hasDraft }: { generating: boolean; hasDraft: boolean }) {
  // SELECTIVE subscription: destructuring the whole store re-rendered this
  // pane on every store write — one per stream event during generation.
  const allegations = useAnalysisStore((s) => s.allegations)
  // Long allegation texts render clamped; a click on the title expands them.
  const [expandedTitles, setExpandedTitles] = useState<Record<string, boolean>>({})
  const [showAll, setShowAll] = useState(false)

  // Render whenever we have allegations — they're populated early (from the
  // aligned set) and persisted per case, so the tab stays meaningful during
  // generation, after completion, and across navigation/reload. The empty copy
  // distinguishes: generating, a finished draft with no saved analysis, or
  // nothing yet.
  if (allegations.length === 0) {
    const title = generating
      ? "Analysing the allegations…"
      : hasDraft
        ? "No analysis saved for this case"
        : "Analysis pending"
    const body = generating
      ? "The case allegations and their findings will appear here as NitiAssist AI works through them."
      : hasDraft
        ? "This draft was generated before allegation analysis was captured — the order on the right is the record. Regenerate to capture the allegation-wise findings."
        : "Allegation-wise findings appear here once the draft has been generated."
    return (
      <div className="empty">
        <div className="empty__ico">
          <Icon name="chart" size={22} />
        </div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    )
  }

  return (
    <div className="pane">
      <div className="pane__inner">
        <div className="uphead" style={{ marginBottom: 14 }}>
          <h3>Allegation analysis</h3>
          <p>{allegations.length} demand{allegations.length > 1 ? "s" : ""} examined.</p>
        </div>
        {(showAll ? allegations : allegations.slice(0, ANALYSIS_ROW_CAP)).map((a, i) => {
          const pill = STATUS_TO_PILL[a.status]
          const rowKey = a.id ?? `A-${i + 1}`
          const isLong = (a.allegation?.length ?? 0) > 160
          const expanded = !isLong || !!expandedTitles[rowKey]
          return (
            <div className="alleg" key={a.id ?? i}>
              <div className="alleg__head">
                <span className="alleg__id mono">{a.id ?? `A-${i + 1}`}</span>
                {isLong ? (
                  <button
                    type="button"
                    className={"alleg__title alleg__title--toggle" + (expanded ? "" : " is-clamped")}
                    onClick={() =>
                      setExpandedTitles((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                    }
                    title={expanded ? "Collapse" : "Show the full allegation"}
                    aria-expanded={expanded}
                  >
                    {a.allegation}
                  </button>
                ) : (
                  <span className="alleg__title">{a.allegation}</span>
                )}
              </div>
              <div className="alleg__foot">
                <span className={"pill pill--" + pill.cls}>{pill.label}</span>
                <span className="alleg__amt">
                  <span className="lbl">Demand</span>
                  {formatAmount(a.amount)}
                </span>
                {a.section && (
                  <span className="alleg__prov mono" style={{ marginLeft: "auto" }}>
                    {a.section}
                  </span>
                )}
              </div>
              <CitationReviewChips
                allegationId={a.id ?? `A${i + 1}`}
                sectionHint={`5.${i + 1}`}
              />
            </div>
          )
        })}
        {!showAll && allegations.length > ANALYSIS_ROW_CAP && (
          <button
            type="button"
            className="btn btn--quiet"
            style={{ marginTop: 10 }}
            onClick={() => setShowAll(true)}
          >
            Show all {allegations.length} rows (may be slow)
          </button>
        )}
      </div>
    </div>
  )
})
